import React, { useState, useEffect } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { acceptInvitation } from "../../../api/user";
import { toast } from "react-hot-toast";

export const AcceptInvitation = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      setError("Invalid link. Token is required.");
    }
  }, [token]);

  const handleAccept = async () => {
    if (!token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await acceptInvitation(token);

      if (response.errors) {
        setError(response.errors.join(", "));
        toast.error(response.errors.join(", "));
      } else {
        setSuccess(true);
        toast.success(response.message || "Invitation accepted successfully!");
        
        // Redirect to dashboard after a short delay
        setTimeout(() => {
          navigate("/dashboard");
        }, 2000);
      }
    } catch (err) {
      const errorMessage =
        err?.response?.data?.errors?.join(", ") ||
        err?.message ||
        "Failed to accept invitation";
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Invalid Link
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {error || "This link is invalid or missing required parameters."}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {success ? "Invitation Accepted!" : "Company Invitation"}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {success
              ? "You have been successfully added to the company. Redirecting..."
              : "You have been invited to join a company in Promang."}
          </p>
        </div>

        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="text-sm text-red-800">{error}</div>
          </div>
        )}

        {success ? (
          <div className="mt-8">
            <div className="rounded-md bg-green-50 p-4">
              <div className="text-sm text-green-800">
                Invitation accepted successfully! Redirecting to dashboard...
              </div>
            </div>
          </div>
        ) : (
          <div className="mt-8">
            <button
              onClick={handleAccept}
              disabled={loading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Accepting..." : "Accept Invitation"}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

